import { Injectable, ViewContainerRef } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, first, map, Observable, of, switchMap } from 'rxjs';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { UserActions } from '@recruitee/account-data';
import type { User } from '@recruitee/account-types';
import { AnalyticsMode } from '@recruitee/analytics';
import { FullCandidateSelectors } from '@recruitee/candidate-data';
import type { ShareOnTheGoRequestPayload } from '@recruitee/candidate-types';
import {
  ShareCandidatesAnalytics,
  ShareCandidatesAnalyticsTrigger,
} from '@recruitee/candidate-util';
import { DevFlag, DevFlagSelectors } from '@recruitee/dev-flags-data';
import { I18nService } from '@recruitee/i18n';
import { InterviewEventsActions } from '@recruitee/interview-events-data';
import { InterviewResultRequestActions } from '@recruitee/interview-result-request-data';
import { JobItemsSelectors } from '@recruitee/offers-data';
import type { OfferItem } from '@recruitee/offers-types';
import { OfferKind } from '@recruitee/offers-types';
import { TalentPoolsDataService } from '@recruitee/talent-pools-data';
import type { TalentPool } from '@recruitee/talent-pools-types';
import { DialogService, DialogWidth, NotificationsController } from '@recruitee/user-interface';

import { ShareOnTheGoDialogComponent } from '../components/share-on-the-go-dialog/share-on-the-go-dialog.component';
import {
  AccessValue,
  ShareOnTheGoDialogData,
  ShareOnTheGoDialogResult,
  UserAccessValue,
} from '../components/share-on-the-go-dialog/types';

type GrantAccess = ShareOnTheGoRequestPayload['grantAccess'][number];

@Injectable({ providedIn: 'root' })
export class ShareOnTheGoService {
  constructor(
    private dialogService: DialogService,
    private store: Store,
    private talentPoolsDataService: TalentPoolsDataService,
    private actions$: Actions,
    private notificationsController: NotificationsController,
    private i18n: I18nService,
    private shareCandidatesAnalytics: ShareCandidatesAnalytics,
  ) {
    this.errorOnEvaluationRequestFailure();
    this.trackAddTeamMembers();
    this.refreshAdminsAfterEvaluationRequest();
  }

  public openShareOnTheGoDialogIfPossible({
    shareOnTheGoDialogData,
    viewContainerRef,
  }: {
    shareOnTheGoDialogData: ShareOnTheGoDialogData;
    viewContainerRef: ViewContainerRef;
  }): Observable<ShareOnTheGoDialogResult | null | undefined> {
    return this.canOpenShareOnTheGoDialog$({ shareOnTheGoDialogData }).pipe(
      switchMap(canOpenDialog =>
        canOpenDialog
          ? this.usersWithoutAccessToCandidateAndOffer$(shareOnTheGoDialogData)
          : of([]),
      ),
      switchMap((usersWithoutAccess: User[]) => {
        if (!usersWithoutAccess.length) {
          return of(<ShareOnTheGoDialogResult>{
            userAccessValues: <UserAccessValue[]>[],
          });
        }

        return this.dialogService.openCustom(
          ShareOnTheGoDialogComponent,
          {
            width: DialogWidth.Small,
          },
          {
            ...shareOnTheGoDialogData!,
            usersToShareWith: usersWithoutAccess,
            allSelectedUsers: shareOnTheGoDialogData!.usersToShareWith,
          },
          viewContainerRef,
        ).afterClosed;
      }),
      first(),
    );
  }

  public resultToRequestPayload({
    shareOnTheGoResult,
    offerId,
    talentPoolId,
    candidateId,
  }: {
    shareOnTheGoResult: ShareOnTheGoDialogResult;
    offerId?: number;
    talentPoolId?: number;
    candidateId: number;
  }): Observable<ShareOnTheGoRequestPayload> {
    const placementId$ = this.getPlacementId$({ candidateId, offerId, talentPoolId });
    const offer$ = offerId
      ? this.store.select(JobItemsSelectors.byId(offerId)).pipe(first())
      : of(null);
    const talentPool$ = this.talentPoolsDataService.collection$().pipe(
      map(
        talentPools =>
          talentPools.find(talentPool => talentPool.id === (offerId || talentPoolId)) ?? null,
      ),
      first(),
    );
    const newTalentPoolsEnabled$ = this.store
      .select(DevFlagSelectors.isEnabled(DevFlag.NewTalentPools))
      .pipe(first());

    return combineLatest([placementId$, offer$, talentPool$, newTalentPoolsEnabled$]).pipe(
      map(([placementId, offer, talentPool, newTalentPoolsEnabled]) =>
        this.createPayload({
          userAccessValues: shareOnTheGoResult.userAccessValues,
          placementId,
          offer,
          talentPool,
          newTalentPoolsEnabled,
        }),
      ),
    );
  }

  private createPayload({
    userAccessValues,
    placementId,
    offer,
    talentPool,
    newTalentPoolsEnabled,
  }: {
    userAccessValues: UserAccessValue[];
    placementId: number | null;
    offer: OfferItem | null;
    talentPool: TalentPool | null;
    newTalentPoolsEnabled: boolean;
  }): ShareOnTheGoRequestPayload {
    return {
      grantAccess: userAccessValues
        .filter(userAccessValue => userAccessValue.accessValue !== AccessValue.DontRequest)
        .map(userAccessValue => {
          const userId = userAccessValue.user.id;

          if (userAccessValue.accessValue === AccessValue.AllCandidatesInOffer) {
            if (offer && offer.kind === OfferKind.Job) {
              return this.createGrantAccessObject(userId, 'offerId', offer.id);
            }

            return this.createGrantAccessObject(
              userId,
              !newTalentPoolsEnabled ? 'offerId' : 'talentPoolId',
              talentPool?.id ?? null,
            );
          }

          return this.createGrantAccessObject(userId, 'placementId', placementId);
        }),
    };
  }

  private createGrantAccessObject<K extends keyof Omit<GrantAccess, 'adminId'>>(
    userId: number,
    grantAccessProp: K,
    grantAccessPropValue: GrantAccess[K],
  ): GrantAccess {
    const base: GrantAccess = {
      adminId: userId,
      offerId: null,
      placementId: null,
      talentPoolId: null,
    };

    return {
      ...base,
      [grantAccessProp]: grantAccessPropValue,
    };
  }

  private usersWithoutAccessToCandidateAndOffer$(
    shareOnTheGoDialogData: ShareOnTheGoDialogData,
  ): Observable<User[]> {
    return this.store
      .select(
        FullCandidateSelectors.offerUsersGrouped({
          candidateId: shareOnTheGoDialogData!.candidateId,
          offerId: shareOnTheGoDialogData!.offerId,
        }),
      )
      .pipe(
        map(({ usersWithoutAccess }) =>
          usersWithoutAccess.filter(user => !user.roleAbilities.includes(Ability.ViewAllJobs)),
        ),
        map(usersWithoutAccess =>
          shareOnTheGoDialogData!.usersToShareWith.filter(userToShareWith =>
            usersWithoutAccess.find(
              userWithoutAccess => userWithoutAccess.id === userToShareWith.id,
            ),
          ),
        ),
        first(),
      );
  }

  private getPlacementId$({
    candidateId,
    offerId,
    talentPoolId,
  }: {
    candidateId: number;
    offerId?: number;
    talentPoolId?: number;
  }): Observable<number | null> {
    return this.store
      .select(FullCandidateSelectors.byIdWithReferences(candidateId))
      .pipe(
        map(
          candidate =>
            candidate?.placements?.find(
              placement =>
                (placement.offerId || placement.talentPoolId) === (offerId || talentPoolId),
            )?.id ?? null,
        ) ?? null,
        first(),
      );
  }

  private canOpenShareOnTheGoDialog$({
    shareOnTheGoDialogData,
  }: {
    shareOnTheGoDialogData: ShareOnTheGoDialogData;
  }): Observable<boolean> {
    return this.store.select(AbilitiesSelectors.hasOne(Ability.ShareCandidates)).pipe(
      map(
        canShareCandidates =>
          canShareCandidates &&
          (shareOnTheGoDialogData?.offerId ?? -1) > 0 &&
          !!shareOnTheGoDialogData?.usersToShareWith.length,
      ),
      first(),
    );
  }

  private errorOnEvaluationRequestFailure(): void {
    this.actions$
      .pipe(ofType(InterviewResultRequestActions.createOnePessimisticFailed))
      .subscribe(() =>
        this.notificationsController.error(
          this.i18n.translate('app.common.error_something_went_wrong'),
        ),
      );
  }

  private trackAddTeamMembers(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewEventsActions.scheduleSuccess,
          InterviewResultRequestActions.createOnePessimisticSuccess,
        ),
      )
      .subscribe(action => {
        if (action.payload.grantAccess?.length) {
          const isScheduleInterview = action.type === InterviewEventsActions.scheduleSuccess.type;

          this.shareCandidatesAnalytics.trackAddTeamMembers({
            teamMembers: action.payload.grantAccess!.length,
            trigger: isScheduleInterview
              ? ShareCandidatesAnalyticsTrigger.ScheduleInterview
              : ShareCandidatesAnalyticsTrigger.EvaluationRequest,
            mode: AnalyticsMode.Single,
          });
        }
      });
  }

  private refreshAdminsAfterEvaluationRequest(): void {
    this.actions$
      .pipe(ofType(InterviewResultRequestActions.createOnePessimisticSuccess))
      .subscribe(action => {
        if (action.payload.grantAccess?.length) {
          this.store.dispatch(UserActions.fetchCollection());
        }
      });
  }
}
